import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Search } from 'lucide-react';
import { Field } from '@/components/catalog/CatalogParts';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useRevokeOnlineAccess, useSetOnlineAccess } from '@/features/onlineAccounts/hooks';
import {
  ONLINE_ACCOUNT_CATEGORY_LABELS,
  SHARE_LEVELS,
  SHARE_LEVEL_LABELS,
  friendlyOnlineAccountError,
  hasLiveAccess,
  localIsoDate,
  profileName,
  type OnlineAccountRow,
} from '@/features/onlineAccounts/model';
import type { SeatProfile } from '@/features/software/model';
import { formatDate } from '@/lib/formatters';
import type { OnlineAccountShareAccess } from '@/types/database';

const NO_ACCESS = '__none';
const SOME_LOGINS = '__logins';

/**
 * Pick one person and set their access on every account at once. Each
 * change saves immediately. Login-by-login sharing stays on the
 * account's own Share dialog.
 */
export function PersonAccountAccessDialog({
  open,
  rows,
  shareable,
  profilesById,
  isOwner,
  onOpenChange,
}: {
  open: boolean;
  /** Accounts the caller can share. */
  rows: OnlineAccountRow[];
  /** Active people only; a deactivated person's shares can only be removed. */
  shareable: SeatProfile[];
  profilesById: Map<string, SeatProfile>;
  isOwner: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const setAccess = useSetOnlineAccess();
  const revoke = useRevokeOnlineAccess();
  const [personId, setPersonId] = useState('');
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPersonId('');
    setSearch('');
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? rows.filter(
          (r) =>
            r.name.toLowerCase().includes(q) ||
            ONLINE_ACCOUNT_CATEGORY_LABELS[r.category].toLowerCase().includes(q),
        )
      : rows;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, search]);

  const personName = profileName(personId, profilesById) ?? 'They';
  const sharedCount = personId
    ? rows.filter((r) => r.people.some((p) => p.profileId === personId && hasLiveAccess(p))).length
    : 0;

  const change = (row: OnlineAccountRow, value: string) => {
    const current = row.people.find((p) => p.profileId === personId);
    setSavingId(row.id);
    const done = () => setSavingId(null);
    if (value === NO_ACCESS) {
      void revoke
        .mutateAsync({ accountId: row.id, profileId: personId })
        .then(() => toast.success(`${row.name} unshared.`))
        .catch((err) => toast.error(friendlyOnlineAccountError(err, 'Could not remove access.')))
        .finally(done);
      return;
    }
    void setAccess
      .mutateAsync({
        accountId: row.id,
        profileId: personId,
        access: value as OnlineAccountShareAccess,
        loginIds: [],
        expiresOn:
          current?.expiresAt && !current.expired ? localIsoDate(current.expiresAt) : '',
      })
      .then(() => toast.success(`${row.name}: ${SHARE_LEVEL_LABELS[value as OnlineAccountShareAccess]}.`))
      .catch((err) => toast.error(friendlyOnlineAccountError(err, 'Could not share the account.')))
      .finally(done);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Access by person</DialogTitle>
          <DialogDescription>
            Pick someone, then choose which accounts they can see. Changes save right away.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Person">
            <Select value={personId} onValueChange={setPersonId}>
              <SelectTrigger>
                <SelectValue placeholder="Pick a person" />
              </SelectTrigger>
              <SelectContent>
                {shareable.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.full_name || p.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {personId && (
              <p className="text-xs text-muted-foreground">
                {sharedCount === 0
                  ? `${personName} can’t see any accounts yet.`
                  : `${personName} can see ${sharedCount} of ${rows.length} ${rows.length === 1 ? 'account' : 'accounts'}.`}
              </p>
            )}
          </Field>

          {personId && (
            <>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search accounts…"
                  className="pl-9"
                  aria-label="Search accounts"
                />
              </div>
              <ul className="divide-y divide-border rounded-lg border border-border">
                {filtered.length === 0 && (
                  <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No accounts match.
                  </li>
                )}
                {filtered.map((row) => {
                  const current = row.people.find((p) => p.profileId === personId);
                  const value = !current
                    ? NO_ACCESS
                    : current.loginIds.length
                      ? SOME_LOGINS
                      : current.access;
                  const locked = !isOwner && current?.access === 'manage';
                  return (
                    <li
                      key={row.id}
                      className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center sm:gap-3"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-sm text-foreground">{row.name}</span>
                          {current?.expired && <Badge variant="muted">Expired</Badge>}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {ONLINE_ACCOUNT_CATEGORY_LABELS[row.category]}
                          {current?.expiresAt && !current.expired
                            ? ` · until ${formatDate(current.expiresAt)}`
                            : ''}
                        </span>
                      </span>
                      <Select
                        value={value}
                        onValueChange={(next) => change(row, next)}
                        disabled={locked || savingId === row.id}
                      >
                        <SelectTrigger className="h-11 w-full sm:w-48" aria-label={`${row.name} access`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_ACCESS}>No access</SelectItem>
                          {SHARE_LEVELS.map((level) => (
                            <SelectItem
                              key={level}
                              value={level}
                              disabled={level === 'manage' && !isOwner}
                            >
                              {SHARE_LEVEL_LABELS[level]}
                            </SelectItem>
                          ))}
                          {value === SOME_LOGINS && (
                            <SelectItem value={SOME_LOGINS} disabled>
                              Specific logins
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
